/**
 * SpotlightPage — public "Priority Roles" micro-site rendered at
 * /spotlight/:companySlug/:campaignSlug.
 *
 * Shareable branded URL for urgent hiring campaigns. Renders:
 *   · Hero: campaign headline + subheadline + CTA + optional hero image
 *   · Highlighted jobs grid (auto-filtered to unexpired spotlights only)
 *   · Testimonials row (name / role / quote / avatar)
 *
 * Uses the anonymous public-theme hook (section "public_apply") so
 * employer branding + colors carry over from the recruiter portal.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { usePublicSectionTheme } from "@/lib/publicTheme";
import { Briefcase, MapPin, Clock, ArrowRight, Building2, Sparkles, ChevronRight } from "lucide-react";

const URGENCY_TONE = {
    "Immediate": { bg: "#DC2626", text: "#fff" },
    "15 days":   { bg: "#F97316", text: "#fff" },
    "30 days":   { bg: "#F59E0B", text: "#111" },
    "Flexible":  { bg: "#E5E7EB", text: "#111" },
};

// ─── OG / meta helpers ──────────────────────────────────────────────────
const INJECTED_META_ATTR = "data-pw-spotlight-meta";
const INJECTED_FONT_ID = "pw-spotlight-font";
const INJECTED_CSS_ID = "pw-spotlight-custom-css";

function setMetaTag(kind, key, value) {
    if (!value) return;
    let el = document.head.querySelector(`meta[${INJECTED_META_ATTR}="${kind}:${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(INJECTED_META_ATTR, `${kind}:${key}`);
        if (kind === "og") el.setAttribute("property", key);
        else el.setAttribute("name", key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", value);
}

function applyOgTags(campaign, company) {
    const title = campaign.og_title || `${campaign.name} · ${company?.name || "Careers"}`;
    const description = campaign.og_description || campaign.subheadline || campaign.headline || "";
    const image = campaign.og_image_url || campaign.hero_image_url || "";
    const url = typeof window !== "undefined" ? window.location.href : "";
    setMetaTag("og", "og:title", title);
    setMetaTag("og", "og:description", description);
    setMetaTag("og", "og:image", image);
    setMetaTag("og", "og:url", url);
    setMetaTag("og", "og:type", "website");
    setMetaTag("tw", "twitter:card", "summary_large_image");
    setMetaTag("tw", "twitter:title", title);
    setMetaTag("tw", "twitter:description", description);
    setMetaTag("tw", "twitter:image", image);
    // Also set the canonical description tag.
    setMetaTag("std", "description", description);
}

const SYSTEM_FONTS = new Set(["Inter", "system-ui", "sans-serif", "serif", "monospace", "Arial", "Helvetica", "Georgia"]);
function injectGoogleFont(family) {
    const first = String(family).split(",")[0].trim().replace(/^['"]|['"]$/g, "");
    if (!first || SYSTEM_FONTS.has(first)) return;
    if (document.getElementById(INJECTED_FONT_ID)) {
        document.getElementById(INJECTED_FONT_ID).href =
            `https://fonts.googleapis.com/css2?family=${encodeURIComponent(first).replace(/%20/g, "+")}:wght@400;500;600;700;800&display=swap`;
    } else {
        const link = document.createElement("link");
        link.id = INJECTED_FONT_ID;
        link.rel = "stylesheet";
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(first).replace(/%20/g, "+")}:wght@400;500;600;700;800&display=swap`;
        document.head.appendChild(link);
    }
    document.body.style.fontFamily = family;
}

function injectCustomCss(css) {
    let el = document.getElementById(INJECTED_CSS_ID);
    if (!el) {
        el = document.createElement("style");
        el.id = INJECTED_CSS_ID;
        document.head.appendChild(el);
    }
    // Minimal sanitisation — strip </style> to prevent early-close attacks.
    el.textContent = String(css).replace(/<\/style>/gi, "");
}

function cleanupInjected() {
    document.head.querySelectorAll(`meta[${INJECTED_META_ATTR}]`).forEach((n) => n.remove());
    document.getElementById(INJECTED_FONT_ID)?.remove();
    document.getElementById(INJECTED_CSS_ID)?.remove();
    document.body.style.fontFamily = "";
}

// Anonymous event tracking — no PII, no CORS drama.
function trackEvent(payload) {
    try {
        api.post("/public/spotlight-events", payload, { withCredentials: false });
    } catch { /* swallow — analytics is best-effort */ }
}

export default function SpotlightPage() {
    const { companySlug, campaignSlug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    usePublicSectionTheme(data?.company?.id, "public_apply");

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get(`/public/spotlight/${companySlug}/${campaignSlug}`, { withCredentials: false });
                setData(data);
                if (data?.campaign?.name) {
                    document.title = data.campaign.og_title || `${data.campaign.name} · ${data.company?.name || "Careers"}`;
                }
                // Inject OG / Twitter meta tags for social previews.
                if (data?.campaign) applyOgTags(data.campaign, data.company);
                // Inject Google Font for the campaign's body_font override.
                if (data?.campaign?.body_font) injectGoogleFont(data.campaign.body_font);
                // Inject custom CSS (Enterprise tier — ungated in beta).
                if (data?.campaign?.custom_css) injectCustomCss(data.campaign.custom_css);
                // Fire an anonymous view event once we've got a payload.
                if (data?.campaign?.id) {
                    trackEvent({ campaign_id: data.campaign.id, event: "view",
                        referrer: document.referrer || null });
                }
            } catch (err) { setError(formatApiError(err)); }
            finally { setLoading(false); }
        })();
        // Clean up injected tags on unmount so navigating away doesn't
        // leave orphan meta/style nodes hanging around.
        return () => { cleanupInjected(); };
    }, [companySlug, campaignSlug]);

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-background text-foreground p-6" data-testid="spotlight-loading">
                <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Loading…</div>
            </div>
        );
    }
    if (error) {
        return (
            <div className="min-h-screen grid place-items-center bg-background text-foreground p-6" data-testid="spotlight-error">
                <div className="max-w-md text-center">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-rose-700">Not available</div>
                    <p className="mt-2 text-sm text-muted-foreground">{error}</p>
                </div>
            </div>
        );
    }
    if (!data) return null;

    const { campaign, company, jobs = [] } = data;
    const accent = campaign.accent_color || "#002FA7";
    const testimonials = campaign.testimonials || [];

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="spotlight-page" data-hero-style={campaign.hero_style || "image"}>
            {/* ── Hero ────────────────────────────────────────────────── */}
            <SpotlightHero campaign={campaign} company={company} accent={accent} jobs={jobs} onCtaClick={() => trackEvent({ campaign_id: campaign.id, event: "cta_click" })} />

            {/* ── Jobs grid ───────────────────────────────────────────── */}
            <section id="roles" className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16" data-testid="spotlight-jobs">
                <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Open roles</div>
                        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Featured this quarter</h2>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                        Updated {new Date(data.generated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </div>
                </div>

                {jobs.length === 0 ? (
                    <div className="border border-border p-10 text-center bg-white/40" data-testid="spotlight-empty">
                        <div className="text-sm text-muted-foreground">
                            No priority roles are open right now.{" "}
                            {company.website && (
                                <a href={company.website} target="_blank" rel="noopener noreferrer" className="underline">
                                    Explore all openings ↗
                                </a>
                            )}
                        </div>
                    </div>
                ) : (
                    <ul className="grid sm:grid-cols-2 gap-4">
                        {jobs.map((j) => {
                            const urg = URGENCY_TONE[j.urgency];
                            const border = j.highlight_color || accent;
                            return (
                                <li key={j.id}>
                                    <Link
                                        to={`/j/${j.slug}?src=spotlight&campaign=${encodeURIComponent(campaign.slug)}`}
                                        onClick={() => trackEvent({ campaign_id: campaign.id, event: "apply_click", job_id: j.id })}
                                        className="group block h-full p-5 sm:p-6 bg-white border-2 hover:border-foreground transition-colors relative"
                                        style={{ borderColor: border }}
                                        data-testid={`spotlight-job-${j.id}`}
                                    >
                                        {j.spotlight_until && (
                                            <span
                                                className="absolute -top-2 left-4 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5"
                                                style={{ background: border, color: "#111", fontWeight: 800 }}
                                            >
                                                ★ Priority
                                            </span>
                                        )}
                                        <h3 className="font-display font-bold text-lg leading-snug pr-8" data-testid={`spotlight-job-title-${j.id}`}>
                                            {j.title}
                                        </h3>
                                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{j.location || "—"}</span>
                                            <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" />{j.work_mode || j.employment_type || "—"}</span>
                                            {j.experience_min != null && j.experience_max != null && (
                                                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{j.experience_min}-{j.experience_max} yrs</span>
                                            )}
                                        </div>
                                        {(j.required_skills || []).slice(0, 5).length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-1">
                                                {(j.required_skills || []).slice(0, 5).map((s) => (
                                                    <span key={s} className="text-[10px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5">
                                                        {s}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div className="mt-4 flex items-center justify-between">
                                            {urg && (
                                                <span
                                                    className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5"
                                                    style={{ background: urg.bg, color: urg.text }}
                                                    data-testid={`spotlight-job-urgency-${j.id}`}
                                                >
                                                    {j.urgency}
                                                </span>
                                            )}
                                            <span className="text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1 group-hover:gap-2 transition-all"
                                                style={{ color: accent }}>
                                                Apply <ArrowRight className="w-3.5 h-3.5" />
                                            </span>
                                        </div>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            {/* ── Custom sections (video, benefits, team, faq, gallery, cta, rich_text, html) ── */}
            <SpotlightSections campaign={campaign} accent={accent} onCta={(sid) => trackEvent({ campaign_id: campaign.id, event: "cta_click", section_id: sid })} />

            {/* ── Testimonials ────────────────────────────────────────── */}
            {testimonials.length > 0 && (
                <section className="border-t border-border bg-secondary/30" data-testid="spotlight-testimonials">
                    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">In their words</div>
                        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-2">From the team you&apos;d join</h2>
                        <ul className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {testimonials.map((t, i) => (
                                <li key={i} className="bg-white border border-border p-5 flex flex-col" data-testid={`spotlight-testimonial-${i}`}>
                                    <blockquote className="text-sm leading-relaxed flex-1">“{t.quote}”</blockquote>
                                    <div className="mt-4 flex items-center gap-3">
                                        {t.avatar_url ? (
                                            <img src={t.avatar_url} alt="" className="w-9 h-9 object-cover"
                                                onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                        ) : (
                                            <div className="w-9 h-9 grid place-items-center bg-secondary text-[10px] font-mono uppercase tracking-widest">
                                                {(t.name || "").split(/\s+/).map((w) => w[0]).join("").slice(0, 2)}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{t.name}</div>
                                            {t.role && <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground truncate">{t.role}</div>}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {/* ── Footer ──────────────────────────────────────────────── */}
            {!campaign.remove_portalwiz_footer && (
            <footer className="border-t border-border">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between gap-4 flex-wrap text-xs text-muted-foreground">
                    <div className="inline-flex items-center gap-2">
                        {company.logo_url && <img src={company.logo_url} alt={company.name} className="h-5 w-auto object-contain" />}
                        <Building2 className="w-3.5 h-3.5" />
                        <span>{company.name}</span>
                    </div>
                    <div className="font-mono uppercase tracking-widest text-[10px]">
                        Powered by <a href="/" className="underline">Portalwiz Talent Solutions</a>
                    </div>
                </div>
            </footer>
            )}
        </div>
    );
}

// ─── Hero variants ──────────────────────────────────────────────────────
function SpotlightHero({ campaign, company, accent, jobs, onCtaClick }) {
    const style = campaign.hero_style || "image";
    /* eslint-disable react/no-unstable-nested-components -- these are scoped
       closures over hero-only props (accent, company, jobs); moving them out
       would require redundant prop drilling for zero rendering benefit on a
       static marketing hero. */
    const Eyebrow = () => (
        <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest mb-4"
            style={{ color: accent }} data-testid="spotlight-eyebrow">
            <Sparkles className="w-3.5 h-3.5" /> Priority hiring · {company.name}
        </div>
    );
    const CTAs = () => (
        <div className="mt-8 flex flex-wrap items-center gap-3">
            {campaign.cta_url && campaign.cta_label && (
                <a
                    href={campaign.cta_url}
                    onClick={onCtaClick}
                    target={/^https?:\/\//i.test(campaign.cta_url) ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 h-11 px-5 font-mono uppercase tracking-widest text-sm hover:opacity-90 transition-opacity"
                    style={{ background: accent, color: "#fff" }}
                    data-testid="spotlight-cta"
                >{campaign.cta_label} <ArrowRight className="w-4 h-4" /></a>
            )}
            <a href="#roles"
                className="inline-flex items-center gap-1 h-11 px-4 border border-border font-mono uppercase tracking-widest text-xs hover:border-foreground"
                data-testid="spotlight-scroll">
                {jobs.length} open role{jobs.length === 1 ? "" : "s"} <ChevronRight className="w-3.5 h-3.5" />
            </a>
        </div>
    );

    if (style === "gradient") {
        return (
            <section className="relative overflow-hidden border-b border-border" data-testid="spotlight-hero" data-variant="gradient">
                <div aria-hidden className="absolute inset-0"
                    style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}88 55%, transparent 100%)`, opacity: 0.9 }} />
                <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-24 text-white">
                    <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest mb-4">
                        <Sparkles className="w-3.5 h-3.5" /> Priority hiring · {company.name}
                    </div>
                    <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl"
                        data-testid="spotlight-headline">{campaign.headline}</h1>
                    {campaign.subheadline && (
                        <p className="mt-5 text-base sm:text-lg opacity-90 max-w-2xl leading-relaxed" data-testid="spotlight-subheadline">
                            {campaign.subheadline}
                        </p>
                    )}
                    <CTAs />
                </div>
            </section>
        );
    }

    if (style === "video" && campaign.hero_video_url) {
        return (
            <section className="relative overflow-hidden border-b border-border" data-testid="spotlight-hero" data-variant="video">
                <VideoBackground url={campaign.hero_video_url} />
                <div aria-hidden className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 0%, #000000cc 100%)` }} />
                <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-24 sm:py-36 text-white">
                    <Eyebrow />
                    <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl"
                        data-testid="spotlight-headline">{campaign.headline}</h1>
                    {campaign.subheadline && (
                        <p className="mt-5 text-base sm:text-lg opacity-95 max-w-2xl leading-relaxed" data-testid="spotlight-subheadline">
                            {campaign.subheadline}
                        </p>
                    )}
                    <CTAs />
                </div>
            </section>
        );
    }

    if (style === "split" && campaign.hero_image_url) {
        return (
            <section className="relative overflow-hidden border-b border-border" data-testid="spotlight-hero" data-variant="split">
                <div className="grid md:grid-cols-2 min-h-[420px]">
                    <div className="relative p-5 sm:p-12 flex flex-col justify-center">
                        <Eyebrow />
                        <h1 className="font-display font-black text-3xl sm:text-5xl leading-[1.05] tracking-tight"
                            data-testid="spotlight-headline">{campaign.headline}</h1>
                        {campaign.subheadline && (
                            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed" data-testid="spotlight-subheadline">
                                {campaign.subheadline}
                            </p>
                        )}
                        <CTAs />
                    </div>
                    <div className="relative min-h-[240px] bg-secondary" aria-hidden style={{ background: `url(${campaign.hero_image_url}) center/cover no-repeat` }} />
                </div>
            </section>
        );
    }

    // Default "image" style
    return (
        <section className="relative overflow-hidden border-b border-border" data-testid="spotlight-hero" data-variant="image">
            {campaign.hero_image_url && (
                <div aria-hidden className="absolute inset-0 opacity-25 bg-cover bg-center"
                    style={{ backgroundImage: `url(${campaign.hero_image_url})` }} />
            )}
            <div className="absolute inset-0 pointer-events-none"
                style={{ background: `linear-gradient(120deg, ${accent}22 0%, transparent 60%)` }} />
            <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-24">
                <Eyebrow />
                <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl"
                    data-testid="spotlight-headline">{campaign.headline}</h1>
                {campaign.subheadline && (
                    <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="spotlight-subheadline">
                        {campaign.subheadline}
                    </p>
                )}
                <CTAs />
            </div>
        </section>
    );
}

function VideoBackground({ url }) {
    // YouTube / Vimeo → embed iframe. MP4 → autoplay muted <video>.
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
    const vim = url.match(/vimeo\.com\/(\d+)/);
    if (yt) {
        return (
            <iframe
                title="Hero background"
                src={`https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&mute=1&controls=0&loop=1&playlist=${yt[1]}&modestbranding=1&playsinline=1`}
                className="absolute inset-0 w-full h-full pointer-events-none scale-125"
                allow="autoplay; encrypted-media"
                allowFullScreen
            />
        );
    }
    if (vim) {
        return (
            <iframe
                title="Hero background"
                src={`https://player.vimeo.com/video/${vim[1]}?autoplay=1&muted=1&loop=1&background=1`}
                className="absolute inset-0 w-full h-full pointer-events-none scale-125"
                allow="autoplay; encrypted-media"
                allowFullScreen
            />
        );
    }
    return (
        <video
            src={url}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
        />
    );
}

// ─── Sections renderer ──────────────────────────────────────────────────
function SpotlightSections({ campaign, accent, onCta }) {
    const sections = (campaign.sections || []).filter((s) => s.enabled !== false);
    if (sections.length === 0) return null;
    return (
        <>
            {sections.map((s) => <SpotlightSection key={s.id} section={s} accent={accent} onCta={onCta} />)}
        </>
    );
}

function SpotlightSection({ section, accent, onCta }) {
    const c = section.config || {};

    if (section.type === "video") {
        const yt = (c.url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
        return (
            <section className="border-t border-border" data-testid={`spotlight-section-${section.id}`} data-section-type="video">
                <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
                    <div className="aspect-video border border-border overflow-hidden bg-black">
                        {yt ? (
                            <iframe
                                title="Video"
                                src={`https://www.youtube-nocookie.com/embed/${yt[1]}`}
                                className="w-full h-full"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                            />
                        ) : (
                            <video src={c.url} controls className="w-full h-full" />
                        )}
                    </div>
                    {c.caption && <p className="mt-3 text-center text-xs text-muted-foreground">{c.caption}</p>}
                </div>
            </section>
        );
    }

    if (section.type === "benefits") {
        return (
            <section className="border-t border-border" data-testid={`spotlight-section-${section.id}`} data-section-type="benefits">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
                    {c.title && <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-6">{c.title}</h2>}
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(c.items || []).map((it, i) => (
                            <li key={i} className="border-l-2 pl-3" style={{ borderColor: accent }}>
                                <div className="text-sm font-medium">{it.label}</div>
                                {it.description && <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{it.description}</p>}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        );
    }

    if (section.type === "team") {
        return (
            <section className="border-t border-border bg-secondary/30" data-testid={`spotlight-section-${section.id}`} data-section-type="team">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
                    {c.title && <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-6">{c.title}</h2>}
                    <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {(c.members || []).map((m, i) => (
                            <li key={i} className="text-center">
                                {m.avatar_url ? (
                                    <img src={m.avatar_url} alt="" className="w-full aspect-square object-cover border border-border"
                                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                ) : (
                                    <div className="w-full aspect-square bg-background border border-border grid place-items-center text-lg font-mono text-muted-foreground">
                                        {(m.name || "").split(/\s+/).map((w) => w[0]).join("").slice(0, 2)}
                                    </div>
                                )}
                                <div className="mt-2 text-sm font-medium">{m.name}</div>
                                {m.role && <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{m.role}</div>}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        );
    }

    if (section.type === "faq") {
        return (
            <section className="border-t border-border" data-testid={`spotlight-section-${section.id}`} data-section-type="faq">
                <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
                    {c.title && <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-6">{c.title}</h2>}
                    <ul className="divide-y divide-border border-y border-border">
                        {(c.items || []).map((it, i) => (
                            <li key={i}>
                                <details className="group">
                                    <summary className="cursor-pointer flex items-center gap-3 py-3 hover:opacity-80">
                                        <span className="text-sm font-medium flex-1">{it.q}</span>
                                        <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
                                    </summary>
                                    <p className="pb-4 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{it.a}</p>
                                </details>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        );
    }

    if (section.type === "gallery") {
        const images = (c.images || []).filter(Boolean);
        return (
            <section className="border-t border-border" data-testid={`spotlight-section-${section.id}`} data-section-type="gallery">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
                    {c.title && <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-6">{c.title}</h2>}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {images.map((url, i) => (
                            <img key={i} src={url} alt="" className="w-full aspect-square object-cover border border-border"
                                onError={(e) => { e.currentTarget.style.display = "none"; }} loading="lazy" />
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (section.type === "cta") {
        return (
            <section className="border-t border-border" data-testid={`spotlight-section-${section.id}`} data-section-type="cta">
                <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16 text-center">
                    {c.headline && <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{c.headline}</h2>}
                    {c.label && c.url && (
                        <a href={c.url}
                            target={/^https?:\/\//i.test(c.url) ? "_blank" : undefined}
                            rel="noopener noreferrer"
                            onClick={() => onCta?.(section.id)}
                            className="inline-flex items-center gap-2 h-11 px-5 mt-6 font-mono uppercase tracking-widest text-sm hover:opacity-90 transition-opacity"
                            style={{ background: accent, color: "#fff" }}
                            data-testid={`spotlight-section-cta-${section.id}`}
                        >{c.label} <ArrowRight className="w-4 h-4" /></a>
                    )}
                    {c.note && <p className="mt-3 text-[11px] text-muted-foreground">{c.note}</p>}
                </div>
            </section>
        );
    }

    if (section.type === "rich_text") {
        const paras = (c.body_markdown || "").split(/\n{2,}/);
        return (
            <section className="border-t border-border" data-testid={`spotlight-section-${section.id}`} data-section-type="rich_text">
                <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
                    {c.title && <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-4">{c.title}</h2>}
                    {paras.map((p, i) => (
                        <p key={i} className="text-sm sm:text-base leading-relaxed mb-4"
                            dangerouslySetInnerHTML={{ __html: renderInlineMd(p) }} />
                    ))}
                </div>
            </section>
        );
    }

    if (section.type === "html") {
        return (
            <section className="border-t border-border" data-testid={`spotlight-section-${section.id}`} data-section-type="html">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8"
                    dangerouslySetInnerHTML={{ __html: String(c.html || "").replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "") }} />
            </section>
        );
    }

    return null;
}

function renderInlineMd(s) {
    // Minimal safe formatter: escape HTML, then apply **bold** and *italic*.
    return String(s)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\*([^*]+)\*/g, "<em>$1</em>")
        .replace(/\n/g, "<br/>");
}
